import { NgClass, TitleCasePipe } from '@angular/common';
import { Component, Input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  EducationalContentViewModel,
  formatEducationalItemLabel,
} from './educational-content-view-model';
import { EducationalContentValueBlockComponent } from './educational-content-value-block.component';
import { EducationalFieldLabelPipe } from './educational-field-label.pipe';

@Component({
  selector: 'app-educational-content-view',
  standalone: true,
  imports: [
    NgClass,
    TitleCasePipe,
    TranslateModule,
    EducationalContentValueBlockComponent,
    EducationalFieldLabelPipe,
  ],
  templateUrl: './educational-content-view.component.html',
  styleUrl: './educational-content-view.component.css',
})
export class EducationalContentViewComponent {
  @Input({ required: true }) view!: EducationalContentViewModel;
  @Input() embedMode = false;
  @Input() artifactKey: string | null = null;

  itemLabel(item: unknown): string {
    return formatEducationalItemLabel(item);
  }

  hasList(values: readonly unknown[] | undefined | null): boolean {
    return Array.isArray(values) && values.length > 0;
  }

  resolvedArtifactKey(): string {
    return this.artifactKey?.trim() || this.view.artifactKey || 'activities';
  }

  primaryLabelKey(): string {
    return this.view.primaryLabelKey || 'knowledgeSetup.sectionLyrics';
  }

  showPrimarySteps(): boolean {
    return this.view.primaryContentKind === 'steps' && this.hasList(this.view.activitySteps);
  }

  showPrimaryBody(): boolean {
    return this.view.primaryContentKind !== 'steps' && this.hasList(this.view.bodyBlocks);
  }

  showTeacherGuidance(): boolean {
    return !!this.view.teacherGuidance?.trim();
  }

  showLegacyActivityInstructions(): boolean {
    return (
      this.view.primaryContentKind !== 'steps' && this.hasList(this.view.activityInstructions)
    );
  }

  extrasEntries(): [string, unknown][] {
    return Object.entries(this.view.extras).sort(([a], [b]) => a.localeCompare(b));
  }

  languageExtraKeys(): string[] {
    const li = this.view.languageIntegration;
    if (!li) return [];
    const skip = new Set(['multilingualSupport', 'localWords']);
    return Object.keys(li)
      .filter(k => !skip.has(k))
      .filter(k => li[k] !== undefined && li[k] !== null && li[k] !== '');
  }

  languageSectionVisible(): boolean {
    const li = this.view.languageIntegration;
    if (!li) return false;
    const ms = li['multilingualSupport'];
    if (typeof ms === 'string' && ms.trim().length > 0) return true;
    if (this.hasList(this.getLocalWords())) return true;
    return this.languageExtraKeys().length > 0;
  }

  getMultilingualSupport(): string | null {
    const li = this.view.languageIntegration;
    if (!li) return null;
    const ms = li['multilingualSupport'];
    return typeof ms === 'string' && ms.trim() ? ms : null;
  }

  getLocalWords(): { word?: string; translation?: string }[] {
    const li = this.view.languageIntegration;
    if (!li) return [];
    const w = li['localWords'];
    return Array.isArray(w) ? (w as { word?: string; translation?: string }[]) : [];
  }

  languageExtraValue(key: string): unknown {
    return this.view.languageIntegration?.[key];
  }

  headingClass(): string {
    return this.embedMode ? 'h6 fw-semibold mb-2' : 'text-lg font-bold text-slate-800 mb-3';
  }

  isRecordValue(v: unknown): v is Record<string, unknown> {
    return v !== null && typeof v === 'object' && !Array.isArray(v);
  }

  hasStandardsContent(): boolean {
    const s = this.view.educationalStandardsAlignment;
    return !!s && Object.keys(s).length > 0;
  }

  hasRawContent(): boolean {
    const c = this.view.rawContent;
    return !!c && Object.keys(c).length > 0;
  }
}
